/**
 * The surface's dressing — docs/27 §3.1's middle clock, spent; §12 item 5.
 *
 * `surfaceSeed` existed before anything read it for content: `SurfaceTerrain`
 * keys the profile's marks on it, and this file is the other half of §3.1's
 * sentence — *"the variations can be authored (a storm, a fallen bough, the
 * pool frozen) rather than sampled, which is the whole thesis of this
 * document."* A dressing is one authored day: what the light is doing, and
 * which of the surface's inhabitants are out in it.
 *
 * **A table, not a noise function.** Three days is not much weather, and that
 * is the point being established rather than a budget being hit: each entry
 * is a *composition* someone chose — fog is dim AND empty of everything but
 * drifting spores, because that is what fog is like — where a sampled system
 * would roll each axis independently and produce bright fog full of chargers.
 * Growing this table is content work with a one-line cost per day, which is
 * exactly the shape #57's conclusion asked variety to take.
 *
 * Pure in `surfaceSeed`, which the profile owns and advances on its own
 * schedule (a milestone, a story beat — never per run). Same seed, same day,
 * across every run and every install: the surface a player left is the
 * surface they return to, weather included, until the world decides
 * otherwise. That is `SurfaceTerrain`'s guard wearing light.
 */

import { hash2 } from '../core/rng';

export interface SurfaceDressing {
  /** Stable name, for tests and prose. */
  readonly id: string;
  /**
   * Multiplier on `DAYLIGHT` above the ground line — `lighting.ts` applies it
   * to the sky branch *and* to the descent-shaft blend's daylight end, so the
   * curve stays continuous at the mouth whatever the day is doing.
   */
  readonly ambient: number;
  /**
   * Which enemies are out. Ids into `enemyData`'s registry; every entry must
   * also carry the `'surface'` biome tag there, and `tests/surfaceDressing`
   * holds the two lists together.
   */
  readonly roster: readonly string[];
}

/**
 * The authored days. Ordered dim-ward; the order carries no meaning beyond
 * readability, since the pick below hashes rather than indexes by schedule.
 */
export const SURFACE_DAYS: readonly SurfaceDressing[] = [
  {
    id: 'clear',
    ambient: 1.0,
    // A clear day is the busy one: the grazer and the thing that stalks it.
    roster: ['fungal_beast', 'bark_lurcher'],
  },
  {
    id: 'overcast',
    ambient: 0.85,
    // Under cloud the lurchers stay in the trees; the shapers come out.
    roster: ['fungal_beast', 'sporecaller'],
  },
  {
    id: 'fogbound',
    ambient: 0.72,
    // Fog is dim and nearly empty — one thing calling through it.
    roster: ['sporecaller'],
  },
];

/** The day this `surfaceSeed` dresses. Pure; the profile owns the clock. */
export function surfaceDressing(surfaceSeed: number): SurfaceDressing {
  return SURFACE_DAYS[hash2(surfaceSeed, 0x5d, 0x140) % SURFACE_DAYS.length];
}
